/**
 * Universal double-click / double-tap directive.
 *
 * Works on both desktop (native double-click) and touch devices (double-tap).
 * Usage:
 *   <div v-double-tap="handler">...</div>
 *   <div v-double-tap:500="handler">...</div>  // custom delay in ms
 */

const DEFAULT_DELAY = 300

export default {
    mounted(el, binding) {
        let lastTap = 0
        let touchHandled = false
        let touchMoved = false
        const delay = Number(binding.arg) || DEFAULT_DELAY

        const touchStartHandler = () => {
            touchMoved = false
        }

        const touchMoveHandler = () => {
            touchMoved = true
        }

        const touchEndHandler = (event) => {
            if (touchMoved) {
                return
            }

            const now = Date.now()
            const tapLength = now - lastTap

            if (tapLength < delay && tapLength > 0) {
                // Prevent zoom on double-tap (iOS / some Android browsers)
                event.preventDefault()
                touchHandled = true
                lastTap = 0

                if (typeof binding.value === 'function') {
                    binding.value(event)
                }
            } else {
                touchHandled = false
                lastTap = now
            }
        }

        const dblClickHandler = (event) => {
            // On touch devices the double-tap is already handled by touchend,
            // so ignore the delayed dblclick/click that may follow.
            if (touchHandled) {
                touchHandled = false
                return
            }

            if (typeof binding.value === 'function') {
                binding.value(event)
            }
        }

        el._doubleTapTouchStart = touchStartHandler
        el._doubleTapTouchMove = touchMoveHandler
        el._doubleTapTouchEnd = touchEndHandler
        el._doubleTapDblClick = dblClickHandler

        el.addEventListener('touchstart', touchStartHandler, { passive: true })
        el.addEventListener('touchmove', touchMoveHandler, { passive: true })
        el.addEventListener('touchend', touchEndHandler, { passive: false })
        el.addEventListener('dblclick', dblClickHandler)
    },

    unmounted(el) {
        if (el._doubleTapTouchStart) {
            el.removeEventListener('touchstart', el._doubleTapTouchStart)
            delete el._doubleTapTouchStart
        }
        if (el._doubleTapTouchMove) {
            el.removeEventListener('touchmove', el._doubleTapTouchMove)
            delete el._doubleTapTouchMove
        }
        if (el._doubleTapTouchEnd) {
            el.removeEventListener('touchend', el._doubleTapTouchEnd)
            delete el._doubleTapTouchEnd
        }
        if (el._doubleTapDblClick) {
            el.removeEventListener('dblclick', el._doubleTapDblClick)
            delete el._doubleTapDblClick
        }
    }
}
